import React, { memo, useCallback, useMemo } from "react";
import "../../css/FullEvent/FullEventInputForm.css";
import "../../css/GlobalStyles.css";
import { CustomTicketV2, Event, PromoCode } from "@markit/common.types";
import {
  getVisibleCustomTickets,
  isCustomTickets,
  isEventTicketsPaid,
  isFreeTickets,
  isPaidTickets,
  isProductSpecificType,
  isRequestTickets,
} from "@markit/common.utils";
import { FormatCurrency } from "../../utils/FormatCurrency";
import { SelectCircleRow } from "../FullEventSubComponents/SelectCircleRow";
import { TicketQuantityBox } from "../FullEventSubComponents/TicketQuantityBox";
import { Colors } from "../../utils/colors";
import { useTheme } from "../../hooks/useTheme";
import FullEventPriceDisplay from "./FullEventPriceDisplay";
import FullEventFlexiblePricingInput from "./FullEventFlexiblePricingInput";

type FullEventTicketOptionsProps = {
  event: Event;
  isRedeemTicket: boolean;
  ticketForRedemption: CustomTicketV2 | undefined;
  setTicketForRedemption: (
    ticketForRedemption: CustomTicketV2 | undefined
  ) => void;
  promoCode: PromoCode | undefined;
  tixId: string;
  selectedCustomTickets: CustomTicketV2[];
  setSelectedCustomTickets: (tickets: CustomTicketV2[]) => void;
  descriptions: string[];
  ticketsInStock: boolean[] | null;
  multiTicketPrompt: (
    customTicket: CustomTicketV2,
    index: number
  ) => React.ReactNode;
  ticketQuantities: Map<string, number>;
  setTicketQuantities: (ticketQuantities: Map<string, number>) => void;
  namedPrices: Map<string, number>;
  setNamedPrices: (namedPrices: Map<string, number>) => void;
  numTicketsUserInGroups: number[];
  disabled: boolean;
  verified: boolean;
  isRedeemFreePromoCode: boolean;
  inputFormErrorPrompt: string | undefined;
  paymentOrRsvpElement?: React.ReactNode;
};

export const FullEventTicketOptions = memo(function FullEventTicketOptionsFn(
  props: FullEventTicketOptionsProps
) {
  const {
    event,
    isRedeemTicket,
    ticketForRedemption,
    promoCode,
    tixId,
    selectedCustomTickets,
    setSelectedCustomTickets,
    descriptions,
    ticketsInStock,
    multiTicketPrompt,
    ticketQuantities,
    setTicketQuantities,
    namedPrices,
    setNamedPrices,
    numTicketsUserInGroups,
    disabled,
    verified,
    isRedeemFreePromoCode,
    inputFormErrorPrompt,
    paymentOrRsvpElement,
  } = props;

  const { theme } = useTheme();

  const ticketsPaid = useMemo(() => isEventTicketsPaid(event), [event]);

  const isProduct = useMemo(
    () => isProductSpecificType(event.eventType),
    [event.eventType]
  );

  const visibleTickets = useMemo(
    () => getVisibleCustomTickets(event.customTickets, ticketForRedemption?.id),
    [event.customTickets, ticketForRedemption?.id]
  );

  /**
   * Disable the specified ticket option if:
   * 1. No more tickets in stock for that group
   * 2. If the number of tickets the user is trying to get is greater than the total ticket quantity
   * 3. If user enters free promo code, disable other ticket options after entering
   * 4. If disabled is true from the passed in prop
   */
  const disabledTicket = useCallback(
    (customTicket: CustomTicketV2, index: number) => {
      return (
        (ticketsInStock && !ticketsInStock[index] && !isRedeemFreePromoCode) ||
        numTicketsUserInGroups[index] >= customTicket.maxQuantity ||
        (isRedeemFreePromoCode &&
          !selectedCustomTickets.some(
            (ticket) => ticket.id === customTicket.id
          )) ||
        disabled
      );
    },
    [
      disabled,
      isRedeemFreePromoCode,
      numTicketsUserInGroups,
      selectedCustomTickets,
      ticketsInStock,
    ]
  );

  const soldOutCustomTicket = useCallback(
    (index: number) => {
      return ticketsInStock !== null && !ticketsInStock[index];
    },
    [ticketsInStock]
  );

  const isSelectedOption = useCallback(
    (customTicket: CustomTicketV2) =>
      selectedCustomTickets.some((ticket) => ticket.id === customTicket.id) ||
      customTicket.id === ticketForRedemption?.id,
    [selectedCustomTickets, ticketForRedemption?.id]
  );

  const updateQuantityOnPress = useCallback(
    (customTicket: CustomTicketV2, quantity: number) => {
      const minQty = customTicket.minQuantity ?? 1;

      const nextTicketQuantities = new Map(ticketQuantities); // ticket quantities
      const nextNamedPrices = new Map(namedPrices); // named prices
      if (quantity < minQty) {
        nextTicketQuantities.delete(customTicket.id);
        setTicketQuantities(nextTicketQuantities);
        setSelectedCustomTickets(
          selectedCustomTickets.filter((t) => t.id !== customTicket.id)
        );
        // Remove the named price for the custom ticket
        setNamedPrices(
          new Map(
            Array.from(namedPrices).filter(([key]) => key !== customTicket.id)
          )
        );
      } else {
        nextTicketQuantities.set(customTicket.id, quantity);
        setTicketQuantities(nextTicketQuantities);
        if (!selectedCustomTickets.some((t) => t.id === customTicket.id)) {
          setSelectedCustomTickets([...selectedCustomTickets, customTicket]);
        }
        // Add the named price for the custom ticket only if it doesn't already exist
        if (!nextNamedPrices.has(customTicket.id)) {
          nextNamedPrices.set(customTicket.id, customTicket.price);
          setNamedPrices(nextNamedPrices);
        }
      }
    },
    [
      ticketQuantities,
      namedPrices,
      setTicketQuantities,
      setSelectedCustomTickets,
      selectedCustomTickets,
      setNamedPrices,
    ]
  );

  return (
    <div
      className="ColumnNormal"
      style={{
        gap: 14,
        opacity: disabled ? 0.4 : 1,
        pointerEvents: disabled ? "none" : "all",
      }}
    >
      <div>
        <div
          className="ColumnNormal"
          style={{
            gap: 4,
            marginBottom: !disabled ? 14 : 0,
          }}
        >
          <span className="NameAndNumber" style={theme.PrimaryText}>
            {isProduct
              ? isRedeemTicket && !isRedeemFreePromoCode
                ? "Redeeming Order"
                : "Select Option"
              : ticketsPaid
              ? isRedeemTicket && !isRedeemFreePromoCode
                ? "Redeeming Ticket"
                : numTicketsUserInGroups.some((numTickets) => numTickets > 0)
                ? "More Tickets"
                : "Tickets"
              : isRequestTickets(event)
              ? "Request"
              : "RSVP"}
          </span>
          {!verified ? (
            <span style={{ color: Colors.GRAY2, fontSize: 12 }}>
              Available after info entered
            </span>
          ) : null}
          {inputFormErrorPrompt ? (
            <span
              style={{ color: Colors.RED3 }}
              className="TicketsExceedCapacity"
            >
              {inputFormErrorPrompt}
            </span>
          ) : null}
        </div>
        {isRedeemTicket && ticketForRedemption ? (
          <>
            <div
              className="ColumnNormalSpaced"
              style={{
                pointerEvents: disabled ? "none" : "all",
                borderColor: theme.FocusedTextFieldBorder.borderColor,
                border: "2px solid",
                borderRadius: 12,
                backgroundColor: theme.SecondaryBG.backgroundColor,
                padding: 14,
                marginTop: 10,
              }}
            >
              <div>
                <div className="AlignedRowSpaced" style={{ marginBottom: 0 }}>
                  {isCustomTickets(event) ? (
                    <p className="TicketText" style={theme.PrimaryText}>
                      {ticketForRedemption.label}
                    </p>
                  ) : isPaidTickets(event) ? (
                    <p className="TicketText" style={theme.PrimaryText}>
                      Paid Ticket
                    </p>
                  ) : (
                    <p className="TicketText" style={theme.PrimaryText}>
                      Free Ticket
                    </p>
                  )}
                  {!isFreeTickets(event) ? (
                    <div
                      className="TicketText"
                      style={{ marginBlock: 5, ...theme.PrimaryText }}
                    >
                      $0
                      <span className="TicketStrike">
                        {FormatCurrency(ticketForRedemption.price / 100)}
                      </span>
                    </div>
                  ) : null}
                </div>
                {multiTicketPrompt(ticketForRedemption, 0)}
                {descriptions[0] ? (
                  <>
                    <span className="smallBodySubtext" style={theme.LabelText}>
                      {descriptions[0].substring(
                        0,
                        descriptions[0].indexOf(".")
                      )}
                    </span>
                    <span className="smallBodySubtext" style={theme.LabelText}>
                      {descriptions[0].substring(
                        descriptions[0].indexOf(".") + 1,
                        descriptions[0].length
                      )}
                    </span>
                  </>
                ) : null}
              </div>
            </div>
          </>
        ) : (
          <div
            style={{
              pointerEvents: disabled ? "none" : "all",
              gap: "7px",
              marginTop: "14px",
            }}
            className="ColumnNormal TicketOptions"
          >
            {visibleTickets.map((customTicket, index) => (
              <SelectCircleRow
                key={index}
                event={event}
                customTicket={customTicket}
                isDisabled={disabledTicket(customTicket, index)}
                isSoldOut={soldOutCustomTicket(index)}
                onPress={() => {}}
                priceTag={
                  isSelectedOption(customTicket) &&
                  customTicket.flexiblePricing ? (
                    <FullEventFlexiblePricingInput
                      customTicket={customTicket}
                      namedPrices={namedPrices}
                      setNamedPrices={setNamedPrices}
                      hardTheme={event.theme}
                    />
                  ) : (
                    <FullEventPriceDisplay
                      eventType={event.eventType}
                      customTicket={customTicket}
                      selectedCustomTicket={
                        selectedCustomTickets.some(
                          (ticket) => ticket.id === customTicket.id
                        )
                          ? customTicket
                          : selectedCustomTickets[0] ?? customTicket
                      }
                      ticketQuantity={
                        ticketQuantities.get(customTicket.id) ?? 0
                      }
                      promoCode={promoCode}
                      isTixId={tixId !== ""}
                      isSoldOut={
                        ticketsInStock !== null && !ticketsInStock[index]
                      }
                      theme={theme}
                    />
                  )
                }
                multiTicketText={multiTicketPrompt(customTicket, index)}
                description={descriptions[index]}
                isSelected={isSelectedOption(customTicket)}
                isCheckout
                rightIcon={
                  !isRedeemTicket ? (
                    <TicketQuantityBox
                      selectedCustomTickets={selectedCustomTickets}
                      ticketQuantity={
                        ticketQuantities.get(customTicket.id) ?? 0
                      }
                      setTicketQuantity={(ticketQuantity) =>
                        updateQuantityOnPress(customTicket, ticketQuantity)
                      }
                      customTicket={customTicket}
                      currTickets={numTicketsUserInGroups[index]}
                    />
                  ) : (
                    <></>
                  )
                }
              />
            ))}
          </div>
        )}
      </div>
      {paymentOrRsvpElement}
    </div>
  );
});
